/**
 * Annotator for SAT passages. A highlight wraps each text node the selected
 * range touches in a <mark> sharing one `data-hl` id, so it works across
 * element boundaries. Highlights and notes live only in the page (not saved),
 * as in the official app, where they are cleared when the module ends.
 */
export const HIGHLIGHT_CLASS = 'exam-highlight';
/** Four highlight colours and an underline (shown last in the panel). */
export const HIGHLIGHT_COLORS = ['yellow', 'green', 'pink', 'blue', 'underline'] as const;
export type HighlightColor = (typeof HIGHLIGHT_COLORS)[number];

/** Highlights `range` inside `root`. Returns false when nothing could be marked. */
export function highlightRange(root: HTMLElement, range: Range, id: string, color: HighlightColor = 'yellow'): boolean {
  if (range.collapsed || !root.contains(range.commonAncestorContainer)) return false;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      range.intersectsNode(node) && node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);

  let marked = false;
  for (const node of nodes) {
    // Never nest highlights or touch rendered math.
    if (node.parentElement?.closest(`.${HIGHLIGHT_CLASS}, .katex, .math-tex`)) continue;
    let target = node;
    const start = node === range.startContainer ? range.startOffset : 0;
    const end = node === range.endContainer ? range.endOffset : node.length;
    if (end <= start) continue;
    if (start > 0) target = target.splitText(start);
    if (end - start < target.length) target.splitText(end - start);
    const mark = document.createElement('mark');
    mark.className = HIGHLIGHT_CLASS;
    mark.dataset.hl = id;
    mark.dataset.color = color;
    target.parentNode?.insertBefore(mark, target);
    mark.appendChild(target);
    marked = true;
  }
  return marked;
}

/** The <mark> elements that make up one highlight. */
export function highlightMarks(root: HTMLElement, id: string): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(`.${HIGHLIGHT_CLASS}[data-hl="${id}"]`));
}

export function setHighlightColor(root: HTMLElement, id: string, color: HighlightColor): void {
  for (const mark of highlightMarks(root, id)) mark.dataset.color = color;
}

/** Attaches a note; the highlight gets a dashed underline and shows the note on hover. */
export function setHighlightNote(root: HTMLElement, id: string, note: string): void {
  const text = note.trim();
  for (const mark of highlightMarks(root, id)) {
    if (text) {
      mark.dataset.note = 'true';
      mark.title = text;
    } else {
      delete mark.dataset.note;
      mark.removeAttribute('title');
    }
  }
}

// Math is skipped so offsets do not move when KaTeX renders after the passage.
const MATH_SELECTOR = '.katex, .math-tex';

/** The passage's text nodes in order, the unit that saved highlight offsets count in. */
function passageTextNodes(root: HTMLElement): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (node.parentElement?.closest(MATH_SELECTOR) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  return nodes;
}

/**
 * Character offsets of `range` in the passage text, so a highlight can be put
 * back after the passage HTML is rendered again. Existing highlights do not
 * change them: splitting and wrapping text keeps the text the same.
 */
export function rangeToOffsets(root: HTMLElement, range: Range): { start: number; end: number } | null {
  if (range.collapsed || !root.contains(range.commonAncestorContainer)) return null;
  let position = 0;
  let start: number | null = null;
  let end: number | null = null;
  for (const node of passageTextNodes(root)) {
    if (range.intersectsNode(node)) {
      const from = node === range.startContainer ? range.startOffset : 0;
      const to = node === range.endContainer ? range.endOffset : node.length;
      if (start === null) start = position + from;
      end = position + to;
    }
    position += node.length;
  }
  return start !== null && end !== null && end > start ? { start, end } : null;
}

/** The range that `rangeToOffsets` described, or null when the text no longer reaches it. */
export function offsetsToRange(root: HTMLElement, start: number, end: number): Range | null {
  if (end <= start) return null;
  const range = document.createRange();
  let position = 0;
  let started = false;
  for (const node of passageTextNodes(root)) {
    const next = position + node.length;
    if (!started && start < next) {
      range.setStart(node, start - position);
      started = true;
    }
    if (started && end <= next) {
      range.setEnd(node, end - position);
      return range;
    }
    position = next;
  }
  return null;
}

/** Removes a highlight, keeping its text. */
export function removeHighlight(root: HTMLElement, id: string): void {
  for (const mark of highlightMarks(root, id)) {
    const parent = mark.parentNode;
    if (!parent) continue;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  }
}
